import {Component, Emitter, Object3D, Texture} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import {CoroutineManager} from '../classes/coroutines/CoroutineManager.js';
import {Coroutine} from '../classes/coroutines/Coroutine.js';
import {NotifyPropertyChanged} from '../utils/NotifyPropertyChanged.js';
import {Localization} from '../utils/Localization.js';
import {
    MalwearRewards,
    RandomUnlockRewards,
    RewardManager,
} from '../classes/RewardManager.js';
import {Statistics} from './statistics.js';
import {RNG} from '../utils/rng.js';
import {TransactionalNames} from '../enums/TransactionalNames.js';
import {MalWearNames} from '../enums/MalWearNames.js';
import {RankManager} from './rank-manager.js';
import {Puzzle} from './puzzle.js';
import {TallyEndTotals} from './tally-end-totals.js';
import {waitForSeconds} from '../classes/coroutines/YieldInstructions.js';
import {formatString} from '../ui/utils/utils.js';
import {formatNumber} from '../utils/Utils.js';

const REWARD_COUNT = 3;
export enum RewardType {
    Unlock,
    Malwear,
}

export class RewardSelections {
    type: RewardType;
    transactionalStat: TransactionalNames;
    rewardObject: string;
    rewardName: string;
    amount: number;
    spriteName: string;
    applyRewardToXP: boolean;
    isCoinReward: boolean;
    malWearName: MalWearNames;
}

export class RewardAwarder extends Component {
    static TypeName = 'reward-awarder';

    private static _instance: RewardAwarder;
    static get instance(): RewardAwarder {
        return RewardAwarder._instance;
    }

    @property.float(0.3)
    malwearProbability = 0.3;

    @property.texture()
    rewardCoins?: Texture;
    @property.texture()
    rewardBombs?: Texture;
    @property.texture()
    rewardClockMag?: Texture;
    @property.texture()
    rewardSkullSmasher?: Texture;
    @property.texture()
    rewardNuke?: Texture;
    @property.texture()
    rewardFruitSmasher?: Texture;
    @property.texture()
    rewardXP?: Texture;
    @property.texture()
    rewardBarriers?: Texture;

    @property.texture()
    X?: Texture;

    propertyChanged: Emitter<[string]> = new Emitter();

    private _rewardDescription: string;
    get rewardDescription(): string {
        return this._rewardDescription;
    }
    private set rewardDescription(value: string) {
        this._rewardDescription = value;
        this.propertyChanged.notify('rewardDescription');
    }

    private _showCloseButton: boolean;
    get showCloseButton(): boolean {
        return this._showCloseButton;
    }
    private set showCloseButton(value: boolean) {
        this._showCloseButton = value;
        this.propertyChanged.notify('showCloseButton');
    }
    private _rewardsGiven: boolean;
    get rewardsGiven(): boolean {
        return this._rewardsGiven;
    }
    private set rewardsGiven(value: boolean) {
        this._rewardsGiven = value;
        this.propertyChanged.notify('rewardsGiven');
    }
    private _selectedReward: number = -1;
    get selectedReward(): number {
        return this._selectedReward;
    }
    private set selectedReward(value: number) {
        this._selectedReward = value;
        this.propertyChanged.notify('selectedReward');
    }

    private _coroutineManager: CoroutineManager = new CoroutineManager();
    // public GameObject buttonClose;

    // public GameObject[] slots;
    // public float rewardScaleFactor;
    // public float malwearProbability;

    malwearObject: string;
    rewardLabels: [string, string, string] = ['', '', ''];
    rewardTextures: [Texture, Texture, Texture] = [null, null, null];

    private _selectedRewards: RewardSelections[] = [];
    private _rewardTextureMap: Map<string, Texture> = new Map();

    init() {
        if (RewardAwarder._instance) {
            console.error('There can only be one instance of RewardAwarder Component');
        }
        RewardAwarder._instance = this;
    }

    start() {
        this._rewardTextureMap = new Map([
            ['Reward Coins', this.rewardCoins],
            ['Reward Bombs', this.rewardBombs],
            ['Reward Barriers', this.rewardBarriers],
            ['Reward Clock Mag', this.rewardClockMag],
            ['Reward Coins', this.rewardCoins],
            ['Reward Fruit Smasher', this.rewardFruitSmasher],
            ['Reward Nuke', this.rewardNuke],
            ['Reward Skull Smasher', this.rewardSkullSmasher],
            ['Reward XP', this.rewardXP],
            //['Reward Malwear', ???],
        ]);
    }

    update(delta: number): void {
        this._coroutineManager.update(delta);
    }

    onActivate(): void {
        this._coroutineManager.stopCoroutine(this._setUpId);
        this._setUpId = this._coroutineManager.addCoroutine(new Coroutine(this.setUp()));
    }

    onDeactivate(): void {
        this._coroutineManager.stopCoroutine(this._setUpId);
        //for (let x = 0; x < this._rewardObjs.Length; x++) {
        // 		Destroy(rewardObjs[x]);
        //}
    }

    private _setUpId: number = 0;
    *setUp() {
        this.rewardsGiven = false;
        this.rewardDescription = Localization.instance.get('RewardScreenDescription');
        this._chooseNewRewards();
        yield null;
        this.showCloseButton = false;
    }

    _chooseNewRewards() {
        const rewardManager = RewardManager.instance;
        let availableRewards: RandomUnlockRewards[] = [];
        for (let x = 0; x < rewardManager.unlockRewards.length; x++) {
            if (
                !rewardManager.unlockRewards[x].requirePrerequisite ||
                (Statistics.instance.getTransactional(
                    rewardManager.unlockRewards[x].prerequisite
                ) != 0 &&
                    rewardManager.unlockRewards[x].implemented)
            ) {
                // prerequisites for this unlock have been met.
                availableRewards.push(rewardManager.unlockRewards[x]);
            }
        }
        let availableMalwearRewards: MalwearRewards[] = null;
        let max = availableRewards.length;
        if (Math.random() <= this.malwearProbability) {
            availableMalwearRewards = [];
            // is there any malwear remaining to get?
            for (let x = 0; x < rewardManager.malwearRewards.length; x++) {
                if (
                    Statistics.instance.getTransactional(
                        rewardManager.malwearRewards[x].transactionalStat
                    ) == 0
                ) {
                    // not yet unlocked.
                    availableMalwearRewards.push(rewardManager.malwearRewards[x]);
                }
            }
            if (availableMalwearRewards.length > 0) {
                max++;
            }
        }
        if (max < REWARD_COUNT) {
            console.error(
                'Could not select the bare minimum of ' +
                    REWARD_COUNT +
                    ' rewards based on prerequisite requirements!'
            );
        }
        const rewardIndexes = RNG.randomNonRepeatingValues(0, max, REWARD_COUNT);
        const biasHighIndex = RNG.getUniformInt(0, rewardIndexes.length - 1);
        // try this x times
        for (let x = 0; x < REWARD_COUNT; x++) {
            const index = rewardIndexes[x];
            this._selectedRewards[x] = new RewardSelections();
            if (index < availableRewards.length) {
                // normal reward
                this._selectedRewards[x].transactionalStat =
                    availableRewards[index].transactionalStat;
                const amountRange =
                    availableRewards[index].highAmount - availableRewards[index].lowAmount;
                const rangeSize = amountRange / 4.0;
                let biasedValue = Math.random() * rangeSize;
                if (biasHighIndex == x) {
                    biasedValue = rangeSize + Math.random() * (rangeSize * 3.0);
                }
                let amount = Math.round(biasedValue + availableRewards[index].lowAmount);
                if (amount > 10000) {
                    amount = amount - (amount % 1000);
                } else if (amount > 1000) {
                    amount = amount - (amount % 100);
                } else if (amount > 100) {
                    amount = amount - (amount % 25);
                }
                this._selectedRewards[x].amount = amount;
                this._selectedRewards[x].rewardName = availableRewards[index].rewardName;
                this._selectedRewards[x].rewardObject =
                    availableRewards[index].rewardObject;
                this._selectedRewards[x].type = RewardType.Unlock;
                this._selectedRewards[x].applyRewardToXP =
                    availableRewards[index].applyRewardToXP;
                this._selectedRewards[x].isCoinReward =
                    availableRewards[index].isCoinReward;
            } else {
                // malwear reward!
                const wearIndex = RNG.getUniformInt(0, availableMalwearRewards.length - 1);
                const wearReward = availableMalwearRewards[wearIndex];
                this._selectedRewards[x].transactionalStat = wearReward.transactionalStat;
                this._selectedRewards[x].spriteName = wearReward.spriteName;
                this._selectedRewards[x].rewardName = wearReward.rewardName;
                this._selectedRewards[x].rewardObject = this.malwearObject;
                this._selectedRewards[x].amount = 1;
                this._selectedRewards[x].type = RewardType.Malwear;
                this._selectedRewards[x].malWearName = wearReward.malWear;
            }
            //Logger.Log(selectedRewards[x].rewardName + " :" + x);
        }
    }

    rewardChosen(reward: number) {
        if (this._selectedRewards[reward].applyRewardToXP) {
            RankManager.instance.addXp(this._selectedRewards[reward].amount, true);
            // juice the tally, if there is one
            const tallyIt = Puzzle.instance.score.object.getComponent(TallyEndTotals);
            tallyIt.addXpReward(this._selectedRewards[reward].amount);
        } else {
            Statistics.instance.addAnonymousPurchaseTransaction(
                this._selectedRewards[reward].transactionalStat,
                this._selectedRewards[reward].amount
            );
            if (this._selectedRewards[reward].isCoinReward == true) {
                //	Logger.Log("adding coins to tally +" + selectedRewards[reward].amount);
                const tallyIt = Puzzle.instance.score.object.getComponent(TallyEndTotals);
                tallyIt.addCoinReward(this._selectedRewards[reward].amount);
            }
        }
        this._coroutineManager.stopCoroutine(this._rewardSequenceId);
        this._rewardSequenceId = this._coroutineManager.addCoroutine(
            new Coroutine(this._rewardSequence(reward))
        );
    }

    private _rewardSequenceId: number = 0;
    private *_rewardSequence(reward: number) {
        yield* waitForSeconds(0.4);

        let format = formatString(
            Localization.instance.get(this._selectedRewards[reward].rewardName),
            formatNumber(this._selectedRewards[reward].amount)
        );

        format = formatString(
            Localization.instance.get('RewardScreenChoseRewardFormat'),
            format
        );
        this.rewardDescription = format;

        // 	rewardObjs[0] = NGUITools.AddChild(slots[reward], selectedRewards[reward].rewardObject);
        // 	UILabel rewardObjLabel = rewardObjs[0].GetComponentInChildren<UILabel>();
        // 	//GIVE OUT REWARDS
        if (this._selectedRewards[reward].type == RewardType.Malwear) {
            // RewardGiftParams rewardParams = rewardObjs[0].GetComponent<RewardGiftParams>();
            // UISprite[] malwearSprites = rewardParams.malWearSprites.GetComponentsInChildren<UISprite>();
            // for (int y = 0; y < malwearSprites.Length; y++)
            // {
            // 	malwearSprites[y].spriteName = selectedRewards[reward].spriteName;
            // }
            // //turn off the sprite
            // BlinkSpriteOnOff blink = rewardParams.didntGetIcon.GetComponent<BlinkSpriteOnOff>();
            // blink.sprite.enabled = false;
            // blink.TurnOff();
            // MalwearEquipHotkey hotkey = rewardObjs[0].GetComponentInChildren<MalwearEquipHotkey>();
            // hotkey.SetUp(selectedRewards[reward].malWearName);
        } else {
            this.rewardLabels[reward] =
                '+' + formatNumber(this._selectedRewards[reward].amount);
            this.rewardTextures[reward] = this._rewardTextureMap.get(
                this._selectedRewards[reward].rewardObject
            );
            //const rewardParams = this._rewardObjs[0].GetComponent<RewardGiftParams>();
            //turn off the sprite
            // BlinkSpriteOnOff blink = rewardParams.didntGetIcon.GetComponent<BlinkSpriteOnOff>();
            // blink.sprite.enabled = false;
            // blink.TurnOff();
        }
        // 	// NO REWARDS FOR YOU
        let rewardObjCount = 1;
        for (let x = 0; x < REWARD_COUNT; x++) {
            if (x != reward) {
                // rewardObjs[rewardObjCount] = NGUITools.AddChild(slots[x], selectedRewards[x].rewardObject);
                // rewardObjLabel = rewardObjs[rewardObjCount].GetComponentInChildren<UILabel>();
                if (this._selectedRewards[x].type == RewardType.Malwear) {
                    // RewardGiftParams rewardParams = rewardObjs[rewardObjCount].GetComponent<RewardGiftParams>();
                    // UISprite[] malwearSprites = rewardParams.malWearSprites.GetComponentsInChildren<UISprite>();
                    // //turn off the scaler
                    // UITweener buttonScale = rewardObjs[rewardObjCount].GetComponentInChildren<UITweener>();
                    // buttonScale.enabled = false;
                    // for (int y = 0; y < malwearSprites.Length; y++)
                    // {
                    // 	malwearSprites[y].spriteName = selectedRewards[x].spriteName;
                    // }
                    // //turn off the EQUIP button
                    // Utils.SetActiveRecursively(rewardParams.turnOffIfDidntGet, false);
                } else {
                    // UITweener buttonScale = rewardObjs[rewardObjCount].GetComponentInChildren<UITweener>();
                    // buttonScale.enabled = false;
                    this.rewardLabels[x] =
                        '+' + formatNumber(this._selectedRewards[x].amount);
                    this.rewardTextures[x] = this._rewardTextureMap.get(
                        this._selectedRewards[x].rewardObject
                    );
                }
                rewardObjCount++;
            }
        }
        this.selectedReward = reward;
        this.showCloseButton = true;
        this.rewardsGiven = true;
        this.propertyChanged.notify('rewardLabels');
        yield null;
    }
}
